import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
} from '@eventbrite/social-login-facebook';
import includes from 'lodash/includes';
import { socialLogIn } from '../api/social';
import {
    GA_APPLE,
    GA_FACEBOOK,
    GA_GOOGLE,
    GA_SOCIAL_PERMISSIONS_GRANTED,
} from '../constants/analytics';
import { IAllContextsProps } from '../constants/interfaces';
import {
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_SOCIAL_ERROR_MESSAGE,
    CREATE_ACCOUNT_MESSAGE,
} from '../constants/messages';
import { trackGeneralAuthAction } from '../tracking/general';
import { trackSocialLoginErrorHeap } from '../tracking/heapTracking';
import { trackAccountAlreadyConnectedWithSocials } from '../tracking/login';
import { trackSocialAction } from '../tracking/social';
import { parseFormPostData } from '../utils';
import { hasSocialAccount } from '../utils/social';
import { socialLogInUserAction } from './social';
import { handleSocialNotAuthorizedError } from './socialErrors';

// Google: steps here first
export const socialLogInUserAuthorizedAction = ({
    socialProvider,
    contexts,
}: {
    socialProvider: string;
    contexts: IAllContextsProps;
}) => {
    if (
        includes(
            [
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
            ],
            socialProvider,
        )
    ) {
        trackSocialAction({
            analytics: contexts.configContext?.analytics,
            action: GA_SOCIAL_PERMISSIONS_GRANTED[socialProvider],
            socialProvider,
        });
    }
};

// Google: steps here second
export const socialConnectVerifiedAction = ({
    socialProvider,
    socialResponse,
    contexts,
}: {
    socialProvider: string;
    socialResponse: any;
    contexts: IAllContextsProps;
}) => {
    const { configContext, notificationsContext } = contexts;
    const socialSSOSettings = configContext?.social?.SSOSettings;

    notificationsContext?.methods.setGeneralNotification({});

    // add param to call api, depending on feature flag
    const skipCreation = socialSSOSettings?.twoStepsSignup || false;

    socialLogInUserAction({
        data: {
            skipCreation,
            ...socialResponse,
        },
        socialProvider,
        contexts,
    });
};

export const socialLogInUserNotAuthorizedAction = ({
    socialProvider,
    socialData,
    contexts,
}: {
    socialProvider?: string;
    socialData: any;
    contexts: IAllContextsProps;
}) => {
    if (socialProvider) {
        const error =
            socialData?.status?.error ?? socialData?.status?.['not_defined'];

        handleSocialNotAuthorizedError({
            error,
            notificationsContext: contexts.notificationsContext,
        });

        trackGeneralAuthAction({
            action: GA_SOCIAL_PERMISSIONS_GRANTED[socialProvider],
            label: error,
            analytics: contexts.configContext?.analytics,
        });

        trackSocialLoginErrorHeap({
            pagePath: 'login',
            socialMethod: socialProvider,
            errorType: (error ?? 'not_defined').toUpperCase(),
        });
    }
};

/**
 * Calling social_action but handling an empty response for it because
 * we only want to store the data on redis to allow other actions like
 * * Forgot password
 * @param {string} email
 * @param {string} accessToken
 */
export const storeSocialDataOnRedis = ({
    email,
    accessToken,
    socialProvider,
}: {
    email: string;
    accessToken: string;
    socialProvider: string;
}) => {
    return socialLogIn(
        parseFormPostData({ email, accessToken, socialProvider }),
    )
        .then(() => {
            Promise.resolve();
        })
        .catch(() => {
            Promise.resolve();
        });
};

export const handleUserIsSocialConnected = ({
    contexts,
}: {
    contexts: IAllContextsProps;
}) => {
    const { configContext, userContext } = contexts;
    const analytics = configContext?.analytics;
    const socialConnected = userContext?.state.lookup.socialConnected;

    const externalActions = configContext?.loginMethods;

    const notificationConfig =
        externalActions?.error?.USER_HAS_NO_PASSWORD?.getNotification?.();
    const extraSocialNotificationProps = {
        extraMessage: notificationConfig?.extraMessage,
        cta: {
            action: notificationConfig?.ctaAction,
            message: notificationConfig?.ctaMessage ?? CREATE_ACCOUNT_MESSAGE,
        },
    };

    // Linked with social
    trackAccountAlreadyConnectedWithSocials({
        analytics,
        connectedSocials: socialConnected,
    });

    checkSocialConnections({
        signInMethods: socialConnected,
        extraSocialNotificationProps,
        contexts,
    });
};

export const checkSocialConnections = ({
    signInMethods,
    extraSocialNotificationProps,
    contexts,
    useLookupSigninMethodSyntax,
}: {
    signInMethods: any;
    extraSocialNotificationProps: any;
    contexts: IAllContextsProps;
    useLookupSigninMethodSyntax?: boolean;
}) => {
    const { notificationsContext, configContext, userContext } = contexts;
    const analytics = configContext?.analytics;
    const socialSSOSettings = configContext?.social?.SSOSettings;

    if (
        // Linked with Facebook and Apple And Google
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_FACEBOOK, GA_APPLE, GA_GOOGLE],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_FACEBOOK, GA_APPLE, GA_GOOGLE],
        });
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([
            GA_FACEBOOK,
            GA_APPLE,
            GA_GOOGLE,
        ]);
    } else if (
        // Linked with Facebook and Google
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_FACEBOOK, GA_GOOGLE],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_FACEBOOK, GA_GOOGLE],
        });
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([
            GA_FACEBOOK,
            GA_GOOGLE,
        ]);
    } else if (
        // Linked with Google and Apple
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_APPLE, GA_GOOGLE],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_APPLE, GA_GOOGLE],
        });
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([GA_GOOGLE, GA_APPLE]);
    } else if (
        // Linked with Facebook and Apple
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_FACEBOOK, GA_APPLE],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_FACEBOOK, GA_APPLE],
        });
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([GA_FACEBOOK, GA_APPLE]);
    } else if (
        // Linked with Facebook
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_FACEBOOK],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_FACEBOOK],
        });

        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([GA_FACEBOOK]);
    } else if (
        // Linked with Apple
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_APPLE],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_APPLE],
        });
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([GA_APPLE]);
    } else if (
        // Linked with Google
        hasSocialAccount({
            socialSSOSettings,
            signInMethods,
            socialToCompare: [GA_GOOGLE],
            useLookupSigninMethodSyntax,
        })
    ) {
        trackAccountAlreadyConnectedWithSocials({
            analytics,
            connectedSocials: [GA_GOOGLE],
        });
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
        userContext?.methods.lookup.setSocialConnected([GA_GOOGLE]);
    } else {
        //It's linked with different social
        notificationsContext?.methods.setGeneralNotification({
            message: ACCOUNT_CONNECTED_TO_SOCIAL_ERROR_MESSAGE,
            ...extraSocialNotificationProps,
        });
    }
};
